// apps/account-portal/src/pages/CustomServicePage.tsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  GlobeAltIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  CloudIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import CheckmarkIcon from '../components/CheckmarkIcon';
import PageFooter from '../components/PageFooter';
import PageContainer from '../components/PageContainer';
import PageTitle from '../components/PageTitle';
import Card from '../components/Card';
import Seo from '../components/Seo';

const CustomServicePage: React.FC = () => {
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  // 服務範圍資料
  const services = [
    {
      icon: GlobeAltIcon,
      title: t('customService.services.web.title'),
      items: [
        t('customService.services.web.item1'),
        t('customService.services.web.item2'),
        t('customService.services.web.item3'),
      ]
    },
    {
      icon: DevicePhoneMobileIcon,
      title: t('customService.services.app.title'),
      items: [
        t('customService.services.app.item1'),
        t('customService.services.app.item2'),
        t('customService.services.app.item3'),
      ]
    },
    {
      icon: ComputerDesktopIcon,
      title: t('customService.services.desktop.title'),
      items: [
        t('customService.services.desktop.item1'),
        t('customService.services.desktop.item2'),
        t('customService.services.desktop.item3'),
      ]
    },
    {
      icon: CloudIcon,
      title: t('customService.services.cloud.title'),
      items: [
        t('customService.services.cloud.item1'),
        t('customService.services.cloud.item2'),
        t('customService.services.cloud.item3'),
      ]
    }
  ];

  // 價格表資料
  const pricing = [
    {
      category: t('customService.pricing.web.category'),
      items: [
        { name: t('customService.pricing.web.item1'), price: t('customService.pricing.web.price1') },
        { name: t('customService.pricing.web.item2'), price: t('customService.pricing.web.price2') },
      ]
    },
    {
      category: t('customService.pricing.app.category'),
      items: [
        { name: t('customService.pricing.app.item1'), price: t('customService.pricing.app.price1') },
        { name: t('customService.pricing.app.item2'), price: t('customService.pricing.app.price2') },
      ]
    },
    {
      category: t('customService.pricing.desktop.category'),
      items: [
        { name: t('customService.pricing.desktop.item1'), price: t('customService.pricing.desktop.price1') },
        { name: t('customService.pricing.desktop.item2'), price: t('customService.pricing.desktop.price2') },
      ]
    },
    {
      category: t('customService.pricing.other.category'),
      items: [
        { name: t('customService.pricing.other.item1'), price: t('customService.pricing.other.price1') },
        { name: t('customService.pricing.other.item2'), price: t('customService.pricing.other.price2') },
      ]
    }
  ];

  // 合作流程
  const process = [
    { step: '01', title: t('customService.process.step1.title'), desc: t('customService.process.step1.desc') },
    { step: '02', title: t('customService.process.step2.title'), desc: t('customService.process.step2.desc') },
    { step: '03', title: t('customService.process.step3.title'), desc: t('customService.process.step3.desc') },
    { step: '04', title: t('customService.process.step4.title'), desc: t('customService.process.step4.desc') },
    { step: '05', title: t('customService.process.step5.title'), desc: t('customService.process.step5.desc') },
    { step: '06', title: t('customService.process.step6.title'), desc: t('customService.process.step6.desc') },
  ];

  // FAQ 資料
  const faqs = [
    { q: t('customService.faq.q1'), a: t('customService.faq.a1') },
    { q: t('customService.faq.q2'), a: t('customService.faq.a2') },
    { q: t('customService.faq.q3'), a: t('customService.faq.a3') },
    { q: t('customService.faq.q4'), a: t('customService.faq.a4') },
    { q: t('customService.faq.q5'), a: t('customService.faq.a5') },
  ];

  return (
    <PageContainer>
      <Seo
        title="客製化軟體外包服務 | RedPanda Tech 小熊貓科技"
        description="提供網站、App、桌面軟體與雲端系統客製化開發服務，依需求量身打造，另有明確報價與合作流程說明。"
        path="/custom-service"
      />
      <PageTitle title={t('customService.hero.title')} subtitle={t('customService.hero.subtitle')} />

      {/* 服務範圍 */}
      <Card className="mb-8">
        <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-6">
          {t('customService.services.title')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => (
            <div key={index}>
              {/* 圖示與標題同一行（R12） */}
              <div className="flex items-center gap-2 mb-3">
                <service.icon className="h-5 w-5 flex-shrink-0 text-[var(--text-secondary)]" />
                <h3 className="text-base font-semibold text-[var(--text-primary)]">
                  {service.title}
                </h3>
              </div>
              <ul className="space-y-2">
                {service.items.map((item, i) => (
                  <li key={i} className="text-sm text-[var(--text-secondary)] flex items-start">
                    <CheckmarkIcon className="w-4 h-4 text-[var(--accent)] mr-2 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Card>

      {/* 參考價格表 */}
      <Card className="mb-8">
        <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-2">
          {t('customService.pricing.title')}
        </h2>
        <p className="text-sm text-[var(--text-secondary)] mb-6">
          {t('customService.pricing.subtitle')}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {pricing.map((category, index) => (
            <div key={index} className="border border-[var(--border-c)] rounded-lg overflow-hidden">
              <div className="bg-[var(--page-bg)] px-4 py-3 border-b border-[var(--border-c)]">
                <h3 className="font-semibold text-[var(--text-primary)]">
                  {category.category}
                </h3>
              </div>
              <div className="divide-y divide-[var(--border-c)]">
                {category.items.map((item, i) => (
                  <div key={i} className="px-4 py-3 flex flex-wrap justify-between items-center gap-x-3 gap-y-1">
                    <span className="text-[var(--text-secondary)]">{item.name}</span>
                    <span className="ml-auto whitespace-nowrap font-semibold tabular-nums text-[var(--text-primary)]">{item.price}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* 合作流程 - Timeline 風格 */}
      <Card className="mb-8">
        <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-8">
          {t('customService.process.title')}
        </h2>

        {/* 桌面版：橫向時間線 */}
        <div className="hidden md:block">
          <div className="relative">
            {/* 連接線 */}
            <div className="absolute top-4 left-0 right-0 h-px bg-[var(--border-c)]" />

            {/* 步驟節點 */}
            <div className="relative flex justify-between">
              {process.map((item, index) => (
                <div key={index} className="flex flex-col items-center" style={{ width: `${100 / process.length}%` }}>
                  {/* 圓形節點 */}
                  <div className="w-8 h-8 bg-[var(--surface)] border border-[var(--accent)] rounded-full flex items-center justify-center text-[var(--accent)] font-semibold text-sm tabular-nums ring-4 ring-[var(--surface)] z-10">
                    {index + 1}
                  </div>
                  {/* 標題 */}
                  <h3 className="mt-4 font-semibold text-[var(--text-primary)] text-sm text-center">
                    {item.title}
                  </h3>
                  {/* 描述 */}
                  <p className="mt-1 text-[13px] text-[var(--text-secondary)] text-center">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 手機版：垂直時間線 */}
        <div className="md:hidden">
          <div className="relative">
            {/* 垂直連接線 */}
            <div className="absolute left-4 top-0 bottom-0 w-px bg-[var(--border-c)]" />

            {/* 步驟列表 */}
            <div className="space-y-6">
              {process.map((item, index) => (
                <div key={index} className="relative flex items-start">
                  {/* 圓形節點 */}
                  <div className="w-8 h-8 bg-[var(--surface)] border border-[var(--accent)] rounded-full flex items-center justify-center text-[var(--accent)] font-semibold text-sm tabular-nums ring-4 ring-[var(--surface)] z-10 flex-shrink-0">
                    {index + 1}
                  </div>
                  {/* 內容 */}
                  <div className="ml-4 pt-1">
                    <h3 className="font-semibold text-[var(--text-primary)]">
                      {item.title}
                    </h3>
                    <p className="mt-1 text-sm text-[var(--text-secondary)]">
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {/* 重要說明 */}
      <Card className="mb-8">
        <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-4 flex items-center">
          <ExclamationTriangleIcon className="h-5 w-5 mr-2 text-amber-500" />
          {t('customService.notice.title')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* 報價包含 */}
          <div>
            <h3 className="font-semibold text-[var(--text-primary)] mb-3 flex items-center">
              {t('customService.notice.included.title')}
            </h3>
            <ul className="space-y-2 text-sm text-[var(--text-secondary)]">
              <li>• {t('customService.notice.included.item1')}</li>
              <li>• {t('customService.notice.included.item2')}</li>
              <li>• {t('customService.notice.included.item3')}</li>
            </ul>
          </div>
          {/* 報價不包含 */}
          <div>
            <h3 className="font-semibold text-[var(--text-primary)] mb-3 flex items-center">
              {t('customService.notice.excluded.title')}
            </h3>
            <ul className="space-y-2 text-sm text-[var(--text-secondary)]">
              <li>• {t('customService.notice.excluded.item1')}</li>
              <li>• {t('customService.notice.excluded.item2')}</li>
              <li>• {t('customService.notice.excluded.item3')}</li>
            </ul>
          </div>
        </div>
        {/* 追加功能計價 */}
        <div className="mt-6 pt-6 border-t border-[var(--border-c)]">
          <h3 className="font-semibold text-[var(--text-primary)] mb-3 flex items-center">
            {t('customService.notice.additional.title')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <div className="font-medium text-[var(--text-primary)]">{t('customService.notice.additional.small')}</div>
              <div className="text-[var(--text-secondary)] tabular-nums">{t('customService.notice.additional.smallPrice')}</div>
            </div>
            <div>
              <div className="font-medium text-[var(--text-primary)]">{t('customService.notice.additional.medium')}</div>
              <div className="text-[var(--text-secondary)] tabular-nums">{t('customService.notice.additional.mediumPrice')}</div>
            </div>
            <div>
              <div className="font-medium text-[var(--text-primary)]">{t('customService.notice.additional.large')}</div>
              <div className="text-[var(--text-secondary)]">{t('customService.notice.additional.largePrice')}</div>
            </div>
          </div>
        </div>
      </Card>

      {/* 常見問題 FAQ */}
      <Card className="mb-8">
        <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-6">
          {t('customService.faq.title')}
        </h2>
        <div className="space-y-3">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border border-[var(--border-c)] rounded-lg overflow-hidden"
            >
              <button
                onClick={() => toggleFaq(index)}
                className="w-full px-4 py-3 flex justify-between items-center text-left bg-[var(--page-bg)] hover:opacity-80 transition-opacity"
              >
                <span className="font-medium text-[var(--text-primary)]">{faq.q}</span>
                {openFaq === index ? (
                  <ChevronUpIcon className="w-5 h-5 text-[var(--text-muted)]" />
                ) : (
                  <ChevronDownIcon className="w-5 h-5 text-[var(--text-muted)]" />
                )}
              </button>
              {openFaq === index && (
                <div className="px-4 py-3 text-[var(--text-secondary)] bg-[var(--surface)]">
                  {faq.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>

      {/* 諮詢前請準備 */}
      <Card className="mb-8">
        <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-4 flex items-center">
          {t('customService.prepare.title')}
        </h2>
        <p className="text-[var(--text-secondary)] mb-4">
          {t('customService.prepare.subtitle')}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h3 className="font-semibold text-[var(--text-primary)] mb-2">
              {t('customService.prepare.required.title')}
            </h3>
            <ul className="space-y-2 text-sm text-[var(--text-secondary)]">
              <li className="flex items-start">
                <span className="text-[var(--text-muted)] mr-2">•</span>
                {t('customService.prepare.required.item1')}
              </li>
              <li className="flex items-start">
                <span className="text-[var(--text-muted)] mr-2">•</span>
                {t('customService.prepare.required.item2')}
              </li>
            </ul>
          </div>
          <div>
            <h3 className="font-semibold text-[var(--text-secondary)] mb-2">
              {t('customService.prepare.optional.title')}
            </h3>
            <ul className="space-y-2 text-sm text-[var(--text-secondary)]">
              <li className="flex items-start">
                <span className="text-[var(--text-muted)] mr-2">•</span>
                {t('customService.prepare.optional.item1')}
              </li>
              <li className="flex items-start">
                <span className="text-[var(--text-muted)] mr-2">•</span>
                {t('customService.prepare.optional.item2')}
              </li>
              <li className="flex items-start">
                <span className="text-[var(--text-muted)] mr-2">•</span>
                {t('customService.prepare.optional.item3')}
              </li>
              <li className="flex items-start">
                <span className="text-[var(--text-muted)] mr-2">•</span>
                {t('customService.prepare.optional.item4')}
              </li>
            </ul>
          </div>
        </div>
      </Card>

      {/* 聯絡我們 */}
      <Card className="text-center">
        <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-4">
          {t('customService.contact.title')}
        </h2>
        <p className="text-[var(--text-secondary)] mb-6 text-balance break-keep">
          {t('customService.contact.subtitle')}
        </p>
        <a
          href="https://line.me/ti/p/~neilshih"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center bg-[var(--accent-fill)] hover:opacity-90 text-[var(--accent-on)] font-bold py-3 px-8 rounded-md transition-colors duration-200"
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63h2.386c.349 0 .63.285.63.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63.349 0 .631.285.631.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.281.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314"/>
          </svg>
          Line {t('customService.contact.consult')}
        </a>
      </Card>

      {/* 頁尾 */}
      <PageFooter />
    </PageContainer>
  );
};

export default CustomServicePage;
